import FlexWrap from '@/components/common/FlexWrap';
import { HOME_CURRENT_STATUS } from '@/constants/home.const';
import { getStatusData } from '@/constants/refundStatus.const';
import { routes } from '@/constants/routes.const';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { IGlobalState } from '@/store';
import { ICON_LOCATION_BLUE } from '@repo/assets';
import {
  BankCallGuideButton,
  CashPickupGuideButton,
  CashPickupIsReceiptDescription,
} from '@repo/components';
import { Icon } from '@successmode-ktp/kui';
import { MouseEvent } from 'react';
import { useHistory } from 'react-router';
import styled, { css, keyframes } from 'styled-components';
import TextButton from '../common/TextButton';
import ProgressBar from './ProgressBar';

function CurrentStatus() {
  const {
    isRefundInfoRegistered,
    paymentStatus,
    unapprovedCount,
    isUploadRequired,
    isReceiptImageUploaded,
    refundAfter,
    nationality,
    refundedIn,
    refundedDate,
    isCashPickup,
    refundAfterAmount,
    refundAmount,
    passportNumber,
  } = useAppSelector((state) => state) as IGlobalState;
  const dispatch = useAppDispatch();
  const isCruise = passportNumber
    ? localStorage.getItem(passportNumber) === 'Cruise'
    : false;

  const { title, description, image, isReceipt, isKorReceipt } = getStatusData({
    paymentStatus,
    unapprovedCount,
    isRefundInfoRegistered,
    isUploadRequired,
    isReceiptImageUploaded,
    nationality,
    refundedIn,
    refundedDate,
    isCashPickup,
    isCruise,
  });
  const history = useHistory();

  const isVisibleInputInfo =
    !isRefundInfoRegistered && refundedIn === 'UNKNOWN';
  const isCashPickupRefundProgressing =
    isCashPickup &&
    isRefundInfoRegistered &&
    paymentStatus === 'PAYMENT_IN_PROGRESS';
  const isVisibleBankInquiryGuide =
    refundedIn === 'UNKNOWN' &&
    (nationality === 'JPN' || nationality === 'CHN');

  const openModal = (e: any) => {
    dispatch({
      type: 'UPDATE_HOME_MODAL_STATE',
      payload: {
        type: 'BANK_INQUIRY_GUIDE',
        isActive: true,
      },
    });
    e.stopPropagation();
  };

  const openCashPickupModal = (e: any) => {
    dispatch({
      type: 'UPDATE_HOME_MODAL_STATE',
      payload: {
        type: 'CASH_PICKUP_GUIDE',
        isActive: true,
      },
    });
    e.stopPropagation();
  };

  const handleViewReceipt = () => {
    if (isUploadRequired) {
      history.push({
        pathname: routes.airportPrintReceipt,
        state: {
          refundAmount: refundAfter ? refundAfterAmount : refundAmount,
        },
      });
    } else {
      history.push({
        pathname: routes.receipt,
        state: {
          refundAmount: refundAfter ? refundAfterAmount : refundAmount,
        },
      });
    }
  };

  return (
    <Container
      refundAfter={refundAfter}
      isRefundInfoRegistered={isRefundInfoRegistered}
    >
      {refundAfter && <ProgressBar />}
      <FlexWrap
        gap='9px'
        justifyContent='space-between'
        style={{ width: '100%', marginTop: 24 }}
      >
        <FlexWrap dir='column' style={{ width: '100%' }}>
          <ContentWrapper>
            <TitleWrapper>
              <div>
                <Title>
                  {refundAfter
                    ? title
                    : HOME_CURRENT_STATUS.getPreRefundTitle(nationality)}
                </Title>
                <Description>
                  {refundAfter
                    ? description
                    : HOME_CURRENT_STATUS.getPreRefundDescription(nationality)}
                </Description>
              </div>
              <StatusImg
                src={image}
                isAirportComplete={refundedIn === 'UNKNOWN'}
              />
            </TitleWrapper>
            {isCruise && !isReceiptImageUploaded && isRefundInfoRegistered && (
              <WarningText>*Verification may take up to 1-2 months</WarningText>
            )}
          </ContentWrapper>
          {isCashPickup && !isReceipt && (
            <CashPickupGuideButton onClick={openCashPickupModal} />
          )}
          {isReceipt && (
            <>
              <TextButton
                title={HOME_CURRENT_STATUS.receiptButtons.getCustomLocationTitle(
                  nationality,
                )}
                onClick={() => history.push(routes.locationCustoms)}
                color='#246CF6'
                width='100%'
                margin='10px 0'
                bgColor='#E9F0FE'
                fontSize='16px'
                style={{ border: '1px solid #A7C4FB' }}
              />
              <BlinkWrapper>
                <TextButton
                  title={HOME_CURRENT_STATUS.receiptButtons.getViewReceiptTitle(
                    nationality,
                  )}
                  onClick={() =>
                    history.push({
                      pathname: routes.receipt,
                      state: {
                        refundAmount: refundAfter
                          ? refundAfterAmount
                          : refundAmount,
                      },
                    })
                  }
                  color='#fff'
                  width='100%'
                  fontSize='16px'
                  margin='0 0 24px 0'
                />
              </BlinkWrapper>
            </>
          )}
          {isCashPickup && isReceipt && <CashPickupIsReceiptDescription />}
          {isKorReceipt && (
            <>
              {!isCruise && (
                <OfficeButton
                  onClick={() => history.push(routes.locationCustoms)}
                >
                  <Icon.QuestionFilled size={18} fillColor='#CBCCCE' />
                  Where is the Customs office?
                </OfficeButton>
              )}

              {isCruise && (
                <ButtonWrapper>
                  <TextButton
                    title={HOME_CURRENT_STATUS.korReceiptButtons.getCustomLocationTitle(
                      nationality,
                    )}
                    onClick={() => history.push(routes.locationCustomsCruise)}
                    color='#246CF6'
                    width='100%'
                    margin='0 0 10px'
                    bgColor='#E9F0FE'
                    fontSize='16px'
                    style={{ fontWeight: 600, letterSpacing: '-0.32px' }}
                  />
                </ButtonWrapper>
              )}
              <ButtonWrapper>
                <TextButton
                  title={HOME_CURRENT_STATUS.korReceiptButtons.getPrintMyReceiptsTitle(
                    nationality,
                  )}
                  onClick={() => history.push(routes.airportPrintReceipt)}
                  color={isCruise ? '' : '#246CF6'}
                  width='100%'
                  margin='0 0 10px'
                  bgColor={isCruise ? '' : '#E9F0FE'}
                  fontSize='16px'
                  style={{ fontWeight: 600, letterSpacing: '-0.32px' }}
                />
              </ButtonWrapper>
              {!isCruise && (
                <BlinkWrapper>
                  <TextButton
                    title={HOME_CURRENT_STATUS.korReceiptButtons.getUploadReceiptTitle(
                      nationality,
                    )}
                    onClick={() => history.push(routes.uploadReceipt)}
                    color='#fff'
                    width='100%'
                    fontSize='16px'
                    style={{ fontWeight: 600, letterSpacing: '-0.32px' }}
                  />
                </BlinkWrapper>
              )}
            </>
          )}
          {paymentStatus !== 'PAYMENT_COMPLETE' && isReceiptImageUploaded && (
            <>
              {isCruise && (
                <TextButton
                  title={HOME_CURRENT_STATUS.getViewEnteredInformationTitle(
                    nationality,
                  )}
                  onClick={() => history.push(routes.refundInfo)}
                  color='#246CF6'
                  width='100%'
                  margin='0 0 10px'
                  bgColor='#E9F0FE'
                  fontSize='16px'
                  style={{ fontWeight: 600, letterSpacing: '-0.32px' }}
                />
              )}
              <ButtonWrapper>
                <TextButton
                  title={HOME_CURRENT_STATUS.receiptButtons.getViewReceiptTitle(
                    nationality,
                  )}
                  onClick={() =>
                    history.push({
                      pathname: routes.receipt,
                      state: {
                        refundAmount: refundAfter
                          ? refundAfterAmount
                          : refundAmount,
                      },
                    })
                  }
                  color={isCruise ? '' : '#246CF6'}
                  width='100%'
                  margin='0 0 10px'
                  bgColor={isCruise ? '' : '#E9F0FE'}
                  fontSize='16px'
                  style={{ fontWeight: 600, letterSpacing: '-0.32px' }}
                />
              </ButtonWrapper>
            </>
          )}

          {!isCashPickup && isVisibleInputInfo && (
            <>
              <TextButton
                title={HOME_CURRENT_STATUS.receiptButtons.getViewReceiptTitle(
                  nationality,
                )}
                onClick={() =>
                  history.push({
                    pathname: routes.receipt,
                    state: {
                      refundAmount: refundAfter
                        ? refundAfterAmount
                        : refundAmount,
                    },
                  })
                }
                color='#246CF6'
                width='100%'
                margin='0 0 10px'
                bgColor='#E9F0FE'
                fontSize='16px'
                style={{ fontWeight: 600, letterSpacing: '-0.32px' }}
              />
              <TextButton
                title={HOME_CURRENT_STATUS.getInputInfoTitle(
                  nationality,
                  isCashPickup,
                )}
                onClick={() => history.push(routes.customerRefund)}
                color='#fff'
                width='100%'
                fontSize='16px'
              />
            </>
          )}
          {isCashPickup && isVisibleInputInfo && (
            <>
              <TextButton
                title={HOME_CURRENT_STATUS.receiptButtons.getViewReceiptTitle(
                  nationality,
                )}
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  handleViewReceipt();
                }}
                color='#246CF6'
                width='100%'
                fontSize='16px'
                margin='8px 0 0 0'
                bgColor='#E9F0FE'
              />
              <TextButton
                title={HOME_CURRENT_STATUS.getInputInfoTitle(
                  nationality,
                  isCashPickup,
                )}
                onClick={() => history.push(routes.customerRefund)}
                color='#fff'
                width='100%'
                fontSize='16px'
                margin='8px 0 8px 0'
              />
            </>
          )}
          {isCashPickupRefundProgressing && (
            <>
              <TextButton
                title={'View Entered Information'}
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  history.push(routes.refundInfo);
                }}
                color='#246CF6'
                width='100%'
                fontSize='16px'
                margin='8px 0 0 0'
                bgColor='#E9F0FE'
              />
              <TextButton
                title={HOME_CURRENT_STATUS.receiptButtons.getViewReceiptTitle(
                  nationality,
                )}
                onClick={() =>
                  history.push({
                    pathname: routes.receipt,
                    state: {
                      refundAmount: refundAfter
                        ? refundAfterAmount
                        : refundAmount,
                    },
                  })
                }
                color='#fff'
                width='100%'
                fontSize='16px'
                margin='8px 0 8px 0'
              />
            </>
          )}
          {refundAfter &&
            paymentStatus === 'PAYMENT_COMPLETE' &&
            isCashPickup && (
              <TextButton
                title='View Western Union Locations'
                style={{ margin: '16px  0 0 0' }}
                color='#246CF6'
                bgColor='#E9F0FE'
                width='100%'
                fontSize='16px'
                icon={ICON_LOCATION_BLUE}
                onClick={() =>
                  window.open(
                    'https://www.westernunion.com/global-services/find-locations?WUCountry=us&WULanguage=en',
                    '_blank',
                  )
                }
              ></TextButton>
            )}

          {isVisibleBankInquiryGuide && (
            <BankCallGuideButton onClick={openModal} />
          )}
        </FlexWrap>
      </FlexWrap>
    </Container>
  );
}
export default CurrentStatus;

const blinkAnimation = keyframes`
  0% {opacity:1;}
  50% {opacity:0.6;}
  100% {opacity:1;}
  `;

const Container = styled.div<{
  refundAfter: boolean;
  isRefundInfoRegistered: boolean;
}>`
  margin: 24px 0;
  padding: 20px;
  width: 100%;
  border-radius: 20px;
  box-shadow: 0px 0px 20px 0px rgba(174, 191, 204, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${blinkAnimation} 1500ms infinite;
  animation-play-state: ${(props) =>
    props.isRefundInfoRegistered ? 'paused' : 'running'};
  ${(props) =>
    !props.refundAfter &&
    css`
      margin-top: 40px;
    `}
`;

const ContentWrapper = styled.div`
  margin-bottom: 28px;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h3`
  color: var(--KTP-Grayscale-80, var(--KTP-Color-Mono-80, #3a3b3e));
  font-size: 20px;
  font-weight: 700;
  line-height: 140%; /* 28px */
  letter-spacing: -0.4px;
  white-space: pre-wrap;
  margin-bottom: 8px;
`;

const Description = styled.p`
  white-space: pre-wrap;
  word-break: keep-all;
  color: var(--KTP-Grayscale-50, var(--KTP-Color-Mono-50, #80848a));
  font-size: 14px;
  font-weight: 500;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
`;

const StatusImg = styled.img<{ isAirportComplete: boolean }>`
  max-width: 90px;
  height: auto;
`;

const BlinkWrapper = styled.div`
  @keyframes blink {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }

  animation: blink 1500ms ease-in-out infinite;
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

const OfficeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-bottom: 20px;
  color: var(--KTP-Grayscale-Gray_05, #777);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.28px;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
`;

const WarningText = styled.p`
  color: var(--primary-error, #e42939);
  font-size: 14px;
  font-weight: 500;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
`;
